import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Plus } from 'lucide-react';
import { Button, ThemeToggleIcon, ToggleGroup } from '../ui';
import { useAppStore } from '../../context/store';
import { CaseEntryModal } from '../caseEntry';
import { UnitSystem } from '../../types';

const navItems = [
  { path: '/', label: 'Cases' },
  { path: '/compare', label: 'Compare' },
];

export const Header: React.FC = () => {
  const location = useLocation();
  const { comparison, setUnitSystem } = useAppStore();
  const [showCaseEntryModal, setShowCaseEntryModal] = useState(false);

  const isActive = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <header className="border-b border-line bg-canvas">
      <div className="mx-auto flex min-h-16 max-w-7xl flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 sm:gap-x-8 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5" aria-label="ComponentScale home">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" strokeWidth="1.5" aria-hidden="true">
            <rect x="4" y="3" width="10" height="18" rx="1.5" stroke="currentColor" />
            <path d="M7 7h4" stroke="currentColor" />
            <path d="M18.5 3v18M17 3h3M17 21h3" className="stroke-accent" />
          </svg>
          <span className="text-base font-semibold tracking-tight">ComponentScale</span>
        </Link>

        <nav aria-label="Main" className="order-last flex w-full gap-1 sm:order-none sm:w-auto sm:flex-1">
          {navItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                aria-current={active ? 'page' : undefined}
                className={`
                  flex h-10 items-center gap-2 border-b-2 px-3 text-sm font-medium transition-colors
                  ${active ? 'border-fg text-fg' : 'border-transparent text-muted hover:text-fg'}
                `}
              >
                {item.label}
                {item.path === '/compare' && comparison.selectedCases.length > 0 && (
                  <span className="rounded-full bg-accent px-1.5 font-mono text-xs text-accent-fg">
                    {comparison.selectedCases.length}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          <ToggleGroup
            options={[
              { value: 'metric', label: 'mm' },
              { value: 'imperial', label: 'in' },
            ]}
            value={comparison.unitSystem}
            onChange={(v) => setUnitSystem(v as UnitSystem)}
            className="font-mono"
          />
          <ThemeToggleIcon />
          <Button variant="outline" onClick={() => setShowCaseEntryModal(true)} className="hidden sm:inline-flex">
            <Plus size={16} />
            Add case
          </Button>
        </div>
      </div>

      <CaseEntryModal
        isOpen={showCaseEntryModal}
        onClose={() => setShowCaseEntryModal(false)}
      />
    </header>
  );
};
